import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Disc3, ArrowUpRight, Clock, MapPin, Users } from 'lucide-react';
import api from '../../lib/api';
import { formatEventDate } from '../../lib/dateUtils';

/**
 * Home page Open Calls section.
 * Editorial layout that matches the home aesthetic:
 *  - big kicker + headline in the section header
 *  - each call rendered as a card with the linked event's flyer as a backdrop,
 *    deadline, spots and applications count
 *  - CTA button styled like the other home CTAs
 * Renders nothing when there are no active calls.
 */
export default function OpenCallStrip() {
  const [calls, setCalls] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/dj/open-calls')
      .then((res) => { if (mounted) setCalls(res.data?.open_calls || []); })
      .catch(() => { if (mounted) setCalls([]); });
    return () => { mounted = false; };
  }, []);

  if (!calls || calls.length === 0) return null;

  // Show up to 3 cards; the rest are surfaced via the "Ver todas" link
  const featured = calls.slice(0, 3);
  const isSingle = featured.length === 1;

  return (
    <section
      className="relative py-14 sm:py-20 px-4 sm:px-6 lg:px-8"
      data-testid="open-call-strip"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header — same rhythm as the rest of the home */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8 sm:mb-10">
          <div>
            <p
              className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-3"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              <Disc3 size={12} /> Open Call DJs
              <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[9px] tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {calls.length} activa{calls.length === 1 ? '' : 's'}
              </span>
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[0.95] tracking-tighter uppercase max-w-2xl"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Buscamos DJs para{' '}
              <span className="text-[#D4AF37]">tocar en la próxima fiesta.</span>
            </h2>
            <p className="text-white/50 text-sm sm:text-base mt-4 max-w-xl">
              Postulaciones abiertas para warm-ups y sets curados. Si tenés press kit, esta es tu chance.
            </p>
          </div>
          <Link
            to="/portal-dj"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold transition-colors group"
            data-testid="open-call-strip-all-link"
          >
            Ver todas las convocatorias
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        {/* Cards grid */}
        <div className={`grid gap-4 sm:gap-5 ${isSingle ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {featured.map((call, idx) => (
            <OpenCallCard key={call.id} call={call} idx={idx} isSingle={isSingle} />
          ))}
        </div>
      </div>
    </section>
  );
}

function OpenCallCard({ call, idx, isSingle }) {
  const event = call.event || null;
  const deadline = call.deadline ? new Date(call.deadline) : null;
  const eventDate = event?.date ? formatEventDate(event.date, "d 'de' MMM") : null;
  const image = call.image_url || event?.image_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to="/portal-dj"
        className="group relative block h-full overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/60 bg-[#0A0A0A] transition-colors"
        data-testid={`open-call-card-${call.id}`}
      >
        {/* Backdrop image (flyer) */}
        <div className={`relative overflow-hidden ${isSingle ? 'aspect-[16/7]' : 'aspect-[16/10]'}`}>
          {image ? (
            <img
              src={image}
              alt={call.title}
              className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-[1.02] transition-all duration-700"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/25 via-[#121212] to-[#030303]" />
          )}
          {/* Depth gradients */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 via-transparent to-transparent" />

          {/* Top-left badges */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black uppercase tracking-widest">
              <Disc3 size={11} /> Open Call
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="relative p-5 sm:p-6 -mt-16 sm:-mt-20">
          <h3
            className={`font-black text-white leading-tight tracking-tight group-hover:text-[#D4AF37] transition-colors ${isSingle ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {call.title}
          </h3>

          {/* Event context */}
          {event && (
            <p className="mt-2 text-white/60 text-xs sm:text-sm flex items-center gap-x-3 gap-y-1 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                <strong className="text-white/80">{event.title}</strong>
              </span>
              {eventDate && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} className="text-[#D4AF37]" /> {eventDate}
                </span>
              )}
              {event.venue_name && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} className="text-[#D4AF37]" /> {event.venue_name}
                </span>
              )}
            </p>
          )}

          {/* Description */}
          {call.description && (
            <p className="mt-3 text-white/55 text-xs sm:text-sm leading-relaxed line-clamp-2">
              {call.description}
            </p>
          )}

          {/* Footer: deadline / spots + CTA */}
          <div className="mt-5 flex items-center justify-between gap-3 pt-4 border-t border-white/5">
            <div className="text-[11px] text-white/50 flex flex-wrap items-center gap-x-3 gap-y-1">
              {deadline ? (
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} className="text-[#D4AF37]" />
                  Cierra el {deadline.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} className="text-[#D4AF37]" /> Postulaciones abiertas
                </span>
              )}
              {call.spots ? (
                <span>· {call.spots} lugar{call.spots === 1 ? '' : 'es'}</span>
              ) : null}
            </div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-black text-xs font-black uppercase tracking-widest whitespace-nowrap group-hover:gap-2.5 transition-all"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Postulate
              <ArrowUpRight size={13} strokeWidth={2.5} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
